import { Icon } from '@iconify/react'
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Textarea,
  useDisclosure,
} from '@nextui-org/react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from 'src/lib/api'
import TenderDocuments from '../components/tender-documents'
import toast from 'react-hot-toast'
import { useSocketContextData } from 'src/context/socket'
import { PowerDocsEventsName } from 'src/types/SocketEvents'
import AttachmentList from 'src/pages/powerdoc-project-overview/components/AttachmentList'
import OverView from 'src/pages/powerdoc-project-overview/components/Overview'
import { useWorkspaceContextData } from 'src/context/workspace'

const inActiveButtonClass = 'bg-transparent text-default-400 pl-5 pr-5'
const activeButtonClass = 'text-black-500 font-medium bg-white rounded-md  pl-5 pr-5'

const phases = [
  { key: 'ASSIGNED', label: 'Added to client tenders list' },
  { key: 'PROCESSING', label: 'Being handled by powerdocs team' },
  { key: 'WAITING_REVIEW', label: 'Waiting for client validation' },
  { key: 'NEEDS_REVIEW', label: 'Client Requested Changes' },
  { key: 'ACCEPTED', label: 'Document was approved by client' },
  { key: 'REJECTED', label: 'Document was entirely rejected by the client.' },
]
export default function AdminTenderView() {
  const params = useParams()
  const navigate = useNavigate()

  const documentsTypes = useMemo(
    () => [
      {
        title: 'Proposal → Sales',
        description: 'Convince a client to purchase a product or service.',
        documentType: 'PROPOSAL',
        subType: 'Sales Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Proposal → Tender',
        description: 'Bid for public contracts or services by responding to an RFP.',
        documentType: 'PROPOSAL',
        subType: 'Tender Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Proposal → Project',
        description: 'Present a structured plan to execute a specific project.',
        documentType: 'PROPOSAL',
        subType: 'Project Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Proposal → Marketing',
        description: 'Outline strategies to promote a product or service.',
        documentType: 'PROPOSAL',
        subType: 'Marketing Proposal',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Offer',
        description: 'Formalize terms and conditions to initiate a business deal.',
        documentType: 'OFFER',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Quotation',
        description: 'Specify pricing and conditions for goods or services.',
        documentType: 'QUOTATION',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
      {
        title: 'Contract',
        description: 'Establish binding terms for obligations and responsibilities.',
        documentType: 'CONTRACT',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
      {
        title: 'NDA',
        description: 'Secure confidential information through legal protection.',
        documentType: 'NDA',
        subType: '',
        available: false,
        icon: 'lucide:file-text',
      },
      {
        title: 'Custom Type',
        description: 'Create your own custom document.',
        documentType: 'CUSTOM',
        subType: '',
        available: true,
        icon: 'lucide:file-text',
      },
    ],
    [],
  )
  const [tender, setTender] = useState<any>({})
  const [activeTab, setActiveTab] = useState<string>('Documents')
  const [currentPhase, setCurrentPhase] = useState<string>('PROCESSING')
  const [creating, setCreating] = useState<boolean>(false)
  const [documentType, setDocumentType] = useState<any>({})
  const [documentPrompt, setDocumentPrompt] = useState<string>('')
  const [documentTitle, setDocumentTitle] = useState<string>('')
  const getTender = async () => {
    const res = await api.get(`/admin/pipeline/${params.tenderUUID}`)
    setTender(res.data)
    setCurrentPhase(res.data.phase)
  }
  useEffect(() => {
    if (params.tenderUUID) {
      getTender()
    }
  }, [params])
  const { io } = useSocketContextData()
  const handleStateUpdate = async (e) => {
    toast.promise(
      io.emitWithAck(PowerDocsEventsName.ADMIN_SET_PIPELINE_PHASE, {
        token: localStorage.getItem('accessToken'),
        pipelineUUID: params.tenderUUID,
        phase: e.target.value,
      }),
      {
        loading: 'Updating project state',
        success: 'Project state has been updated',
        error: 'Error updating the project state',
      },
    )
    setCurrentPhase(e.target.value)
  }
  const { isOpen, onClose, onOpenChange } = useDisclosure()
  const { activeWorkspace } = useWorkspaceContextData()
  const createNewDocument = async () => {
    setCreating(true)
    await toast.promise(
      io.emitWithAck(PowerDocsEventsName.GENERATE_SUBDOCUMENT, {
        token: localStorage.getItem('accessToken'),
        workspaceUUID: activeWorkspace,
        powerDocUUID: tender.powerdoc.uuid,
        variant: 'CUSTOM',
        subType: '',
        prompt: documentPrompt,
        title: documentTitle,
      }),
      {
        loading: `Generating custom document...`,
        error: 'There was an error generating your document',
        success: 'Document successfully generated!',
      },
    )
    setCreating(false)
    onClose()
  }
  const handleDocumentTypeSelect = async (docType: string, docSubType: string) => {
    if (docType === 'CUSTOM') {
      setDocumentType({
        documentType: docType,
        subType: docSubType,
      })
    } else {
      onClose()
      await toast.promise(
        io.emitWithAck(PowerDocsEventsName.GENERATE_SUBDOCUMENT, {
          token: localStorage.getItem('accessToken'),
          workspaceUUID: activeWorkspace,
          powerDocUUID: tender.powerdoc.uuid,
          variant: docType,
          subType: docSubType,
        }),
        {
          loading: `Generating ${docType.toLowerCase()}...`,
          error: 'There was an error generating your document',
          success: 'Document successfully generated!',
        },
      )
      setDocumentPrompt('')
      setDocumentType({})
    }
  }
  return (
    <div className="flex flex-col px-8 w-full max-w-screen-xl h-screen py-3">
      <Breadcrumbs className={'pt-4'}>
        <BreadcrumbItem onClick={() => navigate('/admin/pipelines')}>
          <Icon icon={'lucide:home'} /> Project pipeline
        </BreadcrumbItem>
      </Breadcrumbs>
      <div className="flex w-full pt-2 pb-6">
        <header className="flex flex-col w-full gap-4">
          <div className="w-fit flex flex-row items-center gap-2">
            <div className={'w-fit flex flex-row items-center gap-2 min-h-[36px]'}>
              <h1 className="text-xl font-bold text-default-900 capitalize">Project ▸ {tender?.powerdoc?.title}</h1>
            </div>
          </div>
        </header>
      </div>
      <div className="flex flex-1 w-full pb-2 max-w-full max-h-full overflow-y-auto flex-col">
        <div className="flex w-full bg-red flex-row justify-between">
          <div className={'p-1 rounded-xl w-fit bg-default-100'}>
            {['Documents', 'Overview', 'Attachments'].map((item) => (
              <Button
                key={item}
                size={'sm'}
                className={activeTab.toLowerCase() === item.toLowerCase() ? activeButtonClass : inActiveButtonClass}
                onPress={() => {
                  setActiveTab(item)
                }}
              >
                {item}
              </Button>
            ))}
          </div>
          <div className="flex flex-row items-center gap-4">
            <Select
              selectedKeys={[currentPhase]}
              className="w-[250px]"
              label={currentPhase ? '' : 'Select project phase'}
              size="sm"
              onChange={handleStateUpdate}
            >
              {phases.map((phase) => (
                <SelectItem key={phase.key}>{phase.label}</SelectItem>
              ))}
            </Select>
            <Button
              onPress={() => {
                onOpenChange()
              }}
              color="secondary"
              size="md"
              radius="md"
              isDisabled={false}
              startContent={
                creating ? (
                  <Spinner size={'md'} />
                ) : (
                  <Icon className="flex-none" icon="solar:add-circle-linear" width={20} />
                )
              }
            >
              Create document
            </Button>
          </div>
        </div>
        {activeTab === 'Documents' && <TenderDocuments tender={tender} getTender={getTender} />}
        {activeTab === 'Overview' && (
          <OverView
            doc={tender.powerdoc}
            setDoc={(powerdoc: any) => {
              setTender((prev: any) => ({
                ...prev,
                powerdoc,
              }))
            }}
          />
        )}
        {activeTab === 'Attachments' && (
          <AttachmentList
            doc={tender.powerdoc}
            setDoc={(powerdoc: any) => {
              setTender((prev: any) => ({
                ...prev,
                powerdoc,
              }))
            }}
          />
        )}
        <Modal isOpen={isOpen} onClose={onClose} size="3xl">
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className="flex flex-col gap-1">Create PowerDoc</ModalHeader>
                <ModalBody>
                  <div className="flex flex-col gap-8 p-2">
                    {documentType.documentType === 'CUSTOM' ? (
                      <div>
                        <Input
                          isRequired
                          label="Document Type"
                          labelPlacement="outside"
                          variant="bordered"
                          placeholder="e.g. Redesign of running app 'Explorun'"
                          classNames={{ label: 'text-secondary' }}
                          value={documentTitle ?? ''}
                          onChange={(e) => {
                            const value = e.target.value
                            setDocumentTitle(value)
                          }}
                        />
                        <Textarea
                          minRows={4}
                          maxRows={6}
                          variant="bordered"
                          labelPlacement="outside"
                          label="Document description"
                          value={documentPrompt}
                          onChange={(e) => {
                            setDocumentPrompt(e.target.value)
                          }}
                          className="mt-1 block w-full"
                          placeholder="e.g. This document should provide an executive summary of the company activities during the last 10 years."
                          description="Describe what this document is about"
                          rows={4}
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-4">
                        {documentsTypes.map((documentOption: any) => (
                          <div
                            key={documentOption.title}
                            className={`flex flex-col gap-3 border-3 p-4 py-8 rounded-lg bg-default-200 ${
                              documentOption.available ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                            } ${
                              documentType.documentType === documentOption.documentType &&
                              documentType.subType === documentOption.subType
                                ? 'border-secondary'
                                : 'border-default-200'
                            }`}
                            onClick={() =>
                              documentOption.available
                                ? handleDocumentTypeSelect(documentOption.documentType, documentOption.subType)
                                : ''
                            }
                          >
                            {documentOption.available ? (
                              <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
                                <Icon icon={documentOption.icon} width="20" />
                              </div>
                            ) : (
                              <div className="flex justify-between">
                                <div className="border rounded-xl bg-default-100 border-none p-3 w-fit">
                                  <Icon icon="lucide:file-lock" width="20" />
                                </div>
                                <Chip color="secondary">Coming soon</Chip>
                              </div>
                            )}
                            <div>
                              <h3 className="text-lg font-semibold">{documentOption.title}</h3>
                              <p className="text-sm text-default-500">{documentOption.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </ModalBody>
                <ModalFooter>
                  <div className="flex w-full justify-end gap-2">
                    {
                      <Button
                        onPress={() => {
                          createNewDocument()
                        }}
                        color="secondary"
                        size="md"
                        radius="md"
                        isDisabled={creating}
                        startContent={<Icon className="flex-none" icon="solar:add-circle-linear" width={20} />}
                      >
                        Create document
                      </Button>
                    }
                  </div>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
      </div>
    </div>
  )
}
